// Condições de venda exibidas na loja (cards e página de detalhes).
//
// Tudo que aparece aqui como desconto, parcelamento ou condição de entrega é configurado na
// Precificação do Gerenciador (Condições de Venda gerais + coluna "Condições de venda" de cada
// produto) e lido de /api/sales_conditions.php. Sem configuração nada é exibido além do preço.
// A mesma regra é aplicada no servidor (api/order_create.php) na hora de cobrar: Pix/Boleto,
// frete grátis e total do pedido nunca dependem do que o navegador mostra.

const arred2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

export function normalizarProduto(sc) {
  const r = sc && typeof sc === "object" ? sc : {};
  const numOuModo = (v, min, max) => {
    if (v === "none") return "none";
    if (v === undefined || v === null || v === "" || v === "general") return "general";
    const n = Number(v);
    return isFinite(n) && n >= min && n <= max ? n : "general";
  };
  const lp = parseFloat(String(r.listPrice === undefined || r.listPrice === null ? "" : r.listPrice).replace(",", "."));
  return {
    listPrice: isFinite(lp) && lp > 0 ? lp : null,
    pix: numOuModo(r.pix, 0.01, 90),
    installments: numOuModo(r.installments, 1, 12),
    freeShipping: ["yes", "no"].includes(r.freeShipping) ? r.freeShipping : "general",
    express: r.express === true || r.express === "true" || r.express === 1,
    offer: r.offer === true || r.offer === "true" || r.offer === 1,
    bestSeller: ["yes", "no"].includes(r.bestSeller) ? r.bestSeller : "auto",
  };
}

// Preço unitário de venda do produto (o mesmo valor usado no carrinho e na cobrança).
export function precoDoProduto(produto) {
  const n = Number(produto && (produto.preco !== undefined ? produto.preco : produto.price));
  return isFinite(n) ? n : 0;
}

export function percentualPix(cfg, produto) {
  const sc = normalizarProduto(produto && produto.salesConditions);
  if (sc.pix === "none") return 0;
  if (typeof sc.pix === "number") return sc.pix;
  if (!cfg || !cfg.pix || !cfg.pix.enabled) return 0;
  return Number(cfg.pix.percent) || 0;
}

export function temFreteGratis(cfg, produto) {
  const sc = normalizarProduto(produto && produto.salesConditions);
  if (sc.freeShipping === "yes") return true;
  if (sc.freeShipping === "no") return false;
  if (!cfg || !cfg.freeShipping || !cfg.freeShipping.enabled) return false;
  return precoDoProduto(produto) >= (Number(cfg.freeShipping.minValue) || 0);
}

// Todas as condições do produto, já calculadas, para cards e página de detalhes.
export function calcularCondicoes(estado, produto) {
  const cfg = (estado && estado.config) || null;
  const rankings = (estado && estado.rankings) || {};
  const preco = precoDoProduto(produto);
  const sc = normalizarProduto(produto && produto.salesConditions);
  const out = {
    preco,
    precoDestaque: preco,
    precoPix: null,
    pixPercent: 0,
    pixLabel: (cfg && cfg.pix && cfg.pix.label) || "à vista no Pix ou Boleto",
    listPrice: null,
    descontoPercent: 0,
    parcelas: null,
    freteGratis: false,
    freteGratisLabel: (cfg && cfg.freeShipping && cfg.freeShipping.label) || "Frete Grátis",
    expressa: sc.express,
    expressaLabel: (cfg && cfg.express && cfg.express.label) || "Entrega Expressa",
    oferta: sc.offer,
    maisVendido: false,
    maisVendidoLabel: (cfg && cfg.bestSeller && cfg.bestSeller.label) || "Mais vendido",
    ranking: null,
    vendidoPor: (cfg && cfg.sellerName) || "Import Mais Brasil",
    ativo: !!cfg,
  };
  if (!cfg || !(preco > 0)) return out;

  // Pix / Boleto
  const pct = percentualPix(cfg, produto);
  if (pct > 0) {
    out.pixPercent = pct;
    out.precoPix = arred2(preco * (1 - pct / 100));
    out.precoDestaque = out.precoPix;
  }

  // Preço "DE" (riscado) + percentual de desconto sobre o preço exibido em destaque
  if (sc.listPrice && sc.listPrice > out.precoDestaque) {
    out.listPrice = sc.listPrice;
    out.descontoPercent = Math.max(1, Math.round(((sc.listPrice - out.precoDestaque) / sc.listPrice) * 100));
  }

  // Parcelamento no cartão (sobre o preço de cartão = preço cadastrado)
  let max = 0;
  if (sc.installments === "none") max = 0;
  else if (typeof sc.installments === "number") max = Math.floor(sc.installments);
  else if (cfg.installments && cfg.installments.enabled) max = Number(cfg.installments.max) || 0;
  if (max >= 2) {
    const minParcela = Number(cfg.installments && cfg.installments.minInstallment) || 0;
    let n = max;
    while (n > 1 && preco / n < minParcela) n--;
    if (n >= 2) {
      out.parcelas = {
        n,
        valor: arred2(preco / n),
        total: preco,
        semJuros: !cfg.installments || cfg.installments.interestFree !== false,
      };
    }
  }

  out.freteGratis = temFreteGratis(cfg, produto);

  // Mais vendido / ranking
  const bs = cfg.bestSeller || {};
  const r = rankings[String(produto && produto.id)];
  if (sc.bestSeller === "yes") out.maisVendido = true;
  else if (sc.bestSeller !== "no" && bs.mode !== "manual" && r && r.rank <= (Number(bs.badgeTop) || 3)) out.maisVendido = true;
  if (sc.bestSeller !== "no" && bs.showRanking !== false && r && r.rank <= (Number(bs.rankingMax) || 20)) {
    out.ranking = { rank: r.rank, grupo: r.group };
  }
  return out;
}

// Parcelas para a tabela "Mais formas de pagamento" (de 1x até o máximo permitido).
export function tabelaParcelas(estado, produto) {
  const c = calcularCondicoes(estado, produto);
  const linhas = [];
  if (!c.parcelas) return linhas;
  for (let n = 1; n <= c.parcelas.n; n++) {
    linhas.push({ n, valor: arred2(c.preco / n), semJuros: c.parcelas.semJuros });
  }
  return linhas;
}

export default { calcularCondicoes, tabelaParcelas, normalizarProduto, percentualPix, temFreteGratis, precoDoProduto };
